import {
  TemporaryBackofficeSessionStatus,
  TemporaryBackofficeSessionType,
} from '@backoffice-frontend/graphql'

/**
 * Minutes the prolong mutation pushes a session's expiry out by. The service
 * decides the amount and does not report it, so this only exists to name it in
 * the UI.
 */
export const SESSION_PROLONG_BY_IN_MINUTES = 30

/**
 * Statuses of a session that has neither expired nor been closed, and whose
 * permissions can therefore still be used.
 */
export const ONGOING_SESSION_STATUSES: string[] = [
  TemporaryBackofficeSessionStatus.Opened,
  TemporaryBackofficeSessionStatus.Active,
]

/**
 * Whether the given actor may end this session from the UI: the session must
 * still be closeable, and the actor must be the one who started it. Ending
 * someone else's session must also be rejected server-side - this only
 * controls what the drawer offers.
 */
export const canCloseSession = (
  session: { status: string; actorEmail: string },
  actorEmail: string,
): boolean =>
  session.actorEmail === actorEmail &&
  session.status !== TemporaryBackofficeSessionStatus.Closed

/**
 * Whether a session impersonates a specific user, as opposed to granting scoped
 * access to an organization. The two are presented differently everywhere: an
 * impersonation session names the impersonated user, a scoped-access one the
 * organization.
 */
export const isImpersonationSession = (session: { type: string }): boolean =>
  session.type === TemporaryBackofficeSessionType.Impersonation

/**
 * Display name of the organization with `organizationId`, falling back to the
 * raw id when it cannot be resolved.
 */
export const getOrganizationName = (
  organizations: readonly { id: string; name: string }[] | undefined,
  organizationId: string,
): string =>
  organizations?.find(organization => organization.id === organizationId)
    ?.name ?? organizationId
